import React, {useMemo} from 'react';
import {useBymoPageContextProps} from '../../../doppe-sdk';
import {
    AsyncValueStates,
    evaluateFunction,
    HtmlObjectResizeMode,
    validateIsEmail,
    validateIsMobilePhone
} from '@wix/devzai-utils-common';
import {DoppeActionTypeContactForm} from '../doppe-action-type-contact-form-metadata';
import {
    doppeHideableValueGetValue,
    doppeHideableValueIsVisible,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../../client-server-common/types/doppe-hideable-value';
import {
    ButtonType,
    Checkbox,
    Icon,
    TextBoxAutoComplete,
    TextBoxTypes,
    useAsyncOperation,
    useFormController,
    useInnerWindowController
} from '@wix/devzai-utils-react';
import {classes, style} from './doppe-action-type-contact-form-widget.st.css';
import {FlexLayout} from '@wix/devzai-common-client';
import IconChecked from '../../../components/subscribe-action-form/icons/checked.svg';
import {DoppeActionWidgetTextBox} from '../../../components/doppe-action-widget-text-box/doppe-action-widget-text-box';
import {DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionWidgetTextArea
} from '../../../components/doppe-action-widget-text-area/doppe-action-widget-text-area';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapper
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper/doppe-action-type-widget-content-layout-new-wrapper';
import {useDoppeViewerLocales} from '../../../client/doppe-viewer-locales/doppe-viewer-locales';
import {DoppePageMediaView} from '../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    DoppeActionTypeWidgetInnerCtaButton
} from '../../../components/doppe-action-type-widget-inner-cta-button/doppe-action-type-widget-inner-cta-button';

export const DoppeActionTypeContactFormWidget = React.memo(function DoppeActionTypeContactFormWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeContactForm.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const action = actionContext.action;

    const actionSettings = action.settings;

    const {
        userDisplayName,
        targetUrl,
    } = useBymoPageContextProps();

    const doppeViewerLocales = useDoppeViewerLocales();

    const {
        showConsentCheckbox,
        customConsentText,
        subscribeButtonText,
        collectPhoneNumber,
        collectEmail,
        collectName,
        collectAddress,
        collectZip,
        collectMessage,
        customThankYouMessage,
        images,
        title,
        description,
    } = actionSettings;

    const consentText = useMemo(() => {
        if (doppeHideableValueIsVisible(customConsentText)) {
            const customConsentTextValue = doppeHideableValueGetValue(customConsentText).trim();

            if (customConsentTextValue !== '') {
                return customConsentTextValue
            }
        }

        if (showConsentCheckbox) {
            return doppeViewerLocales['subscribeCheckBoxText']({
                username: userDisplayName
            })
        } else {
            return doppeViewerLocales['subscribeConsentText']({
                username: userDisplayName
            })
        }
    }, [customConsentText, userDisplayName, showConsentCheckbox]);

    const [formSubmissionOperation, setFormSubmissionOperation] = useAsyncOperation();

    const checkboxId = `${action.id}_checkbox`;

    const formController = useFormController<{
        email: string;
        contactName: string;
        phoneNumber: string;
        address: string;
        message: string;
        zip: string;
        hasConsent: boolean;
    }>({
        email: '',
        contactName: '',
        phoneNumber: '',
        address: '',
        zip: '',
        message: '',
        hasConsent: false
    }, {
        validationFunction: (_formData, errorsReporter) => {

            if (collectEmail) {
                errorsReporter.report('email', email => {
                    if (email === '') {
                        return doppeViewerLocales['requiredField'];
                    }

                    if (!validateIsEmail(email)) {
                        return doppeViewerLocales['enterValidEmail'];
                    }

                    return undefined;
                })
            }

            if (collectPhoneNumber) {
                errorsReporter.report('phoneNumber', phoneNumber => {
                    if (phoneNumber === '') {
                        return doppeViewerLocales['requiredField'];
                    }

                    if (!validateIsMobilePhone(phoneNumber)) {
                        return doppeViewerLocales['enterValidPhone'];
                    }

                    return undefined;
                })
            }

            if (collectName) {
                errorsReporter.report('contactName', contactName => {
                    if (contactName === '') {
                        return doppeViewerLocales['requiredField'];
                    }

                    return undefined;
                })
            }

            if (collectAddress) {
                errorsReporter.report('address', address => {
                    if (address === '') {
                        return doppeViewerLocales['requiredField'];
                    }

                    return undefined;
                })
            }

            if (collectZip) {
                errorsReporter.report('zip', zip => {
                    if (zip === '') {
                        return doppeViewerLocales['requiredField'];
                    }

                    return undefined;
                })
            }

            if (collectMessage) {
                errorsReporter.report('message', message => {
                    if (message === '') {
                        return doppeViewerLocales['requiredField'];
                    }

                    return undefined;
                })
            }

            errorsReporter.report('hasConsent', hasConsent => {
                if (showConsentCheckbox && !hasConsent) {
                    return doppeViewerLocales['requiredConsent'];
                }

                return undefined;
            });
        }
    })

    const contactNameError = formController.formFieldsErrors.contactName;
    const messageError = formController.formFieldsErrors.message;
    const emailInputError = formController.formFieldsErrors.email;
    const phoneNumberInputError = formController.formFieldsErrors.phoneNumber;
    const addressInputError = formController.formFieldsErrors.address;
    const zipInputError = formController.formFieldsErrors.zip;

    const windowController = useInnerWindowController();

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {
                hasTextBeforeFields: doppeHideableValueIsVisibleAndNotEqualValue(title, '') || doppeHideableValueIsVisibleAndNotEqualValue(description, ''),
            }, className)}
            actionContext={actionContext}
            isForm={true}
            noValidate={true}
            onSubmit={formController.createSubmitEventHandler(async (formData) => {

                try {
                    await setFormSubmissionOperation(actionContext.submitContactForm({
                        phone: collectPhoneNumber ? formData.phoneNumber : undefined,
                        email: collectEmail ? formData.email : undefined,
                        contactName: collectName ? formData.contactName : undefined,
                        zipCode: collectZip ? formData.zip : undefined,
                        address: collectAddress ? formData.address : undefined,
                        listId: actionSettings.listId ?? undefined,
                        message: collectMessage ? formData.message : undefined,
                    }));

                    formController.resetFormState();

                    if (actionContext.isLockAction()) {
                        actionContext.unlockLockAction();
                    }
                    else {
                        actionContext.reportGoalAchieved();

                        if (windowController) {
                            windowController.closeWindow();
                        }

                        const thankYouText = doppeHideableValueIsVisibleAndNotEqualValue(customThankYouMessage, '') ?
                            doppeHideableValueGetValue(customThankYouMessage) : doppeViewerLocales['formThankYou'];

                        if (targetUrl) {
                            actionContext.redirectToTargetUrl({
                                additionalMessage: thankYouText
                            });
                        } else {
                            actionContext.showNotification({
                                message: thankYouText
                            });
                        }
                    }
                } catch (error) {
                    console.error(error);
                }
            })}
        >
            <DoppeActionTypeWidgetContentLayoutNewWrapper
                fullWidthMainMedia={true}
                mainMedia={images && images.length > 0 ? (
                    <DoppePageMediaView
                        className={style(classes.productMedia, {})}
                        mediaResource={images[0]}
                        layoutSpec={{autoHeight: true, maxHeight: 360}}
                        widthSpec={{contentRelativeWidth: 1}}
                        mediaResizeMode={HtmlObjectResizeMode.Cover}
                    />
                ) : null}
                footer={
                    <div
                        className={style(classes.footer, {}, FlexLayout.column.default)}
                    >
                        <DoppeActionTypeWidgetInnerCtaButton
                            className={style(classes.subscribeButton)}
                            buttonType={ButtonType.SubmitButton}
                            isLoading={formSubmissionOperation?.state === AsyncValueStates.Pending}
                            actionContext={actionContext}
                        >
                            {subscribeButtonText}
                        </DoppeActionTypeWidgetInnerCtaButton>
                    </div>
                }
            >
                <DoppeActionTypeWidgetInnerTitle>
                    {title}
                </DoppeActionTypeWidgetInnerTitle>
                <DoppeActionTypeWidgetInnerDescription
                    className={style(classes.description, {})}
                >
                    {description}
                </DoppeActionTypeWidgetInnerDescription>
                <div
                    className={style(classes.subscribeForm, FlexLayout.column.default)}
                >
                    {collectName ? (
                        <div
                            className={style(classes.formField, {})}
                        >
                            <DoppeActionWidgetTextBox
                                hasError={contactNameError !== undefined}
                                autoComplete={TextBoxAutoComplete.PersonFullName}
                                shouldValidateValue={false}
                                placeholder={doppeViewerLocales['contactFormNameFieldPlaceholder']}
                                value={formController.getValue('contactName')}
                                onTextChange={value => {
                                    formController.setValue('contactName', value);
                                }}
                            />
                            {contactNameError !== undefined ? (
                                <div className={style(classes.inputErrorMessage, {})}>{contactNameError}</div>
                            ) : null}
                        </div>
                    ) : null}
                    {collectEmail ? (
                        <div
                            className={style(classes.formField, {})}
                        >
                            <DoppeActionWidgetTextBox
                                hasError={emailInputError !== undefined}
                                type={TextBoxTypes.Email}
                                shouldValidateValue={false}
                                placeholder={doppeViewerLocales['contactFormEmailFieldPlaceholder']}
                                value={formController.getValue('email')}
                                onTextChange={value => {
                                    formController.setValue('email', value);
                                }}
                            />
                            {emailInputError !== undefined ? (
                                <div className={style(classes.inputErrorMessage, {})}>{emailInputError}</div>
                            ) : null}
                        </div>
                    ) : null}
                    {collectPhoneNumber ? (
                        <div
                            className={style(classes.formField, {})}
                        >
                            <DoppeActionWidgetTextBox
                                type={TextBoxTypes.PhoneNumber}
                                hasError={phoneNumberInputError !== undefined}
                                placeholder={doppeViewerLocales['contactFormPhoneFieldPlaceholder']}
                                shouldValidateValue={false}
                                value={formController.getValue('phoneNumber')}
                                onTextChange={value => {
                                    formController.setValue('phoneNumber', value);
                                }}
                            />
                            {phoneNumberInputError !== undefined ? (
                                <div className={style(classes.inputErrorMessage, {})}>{phoneNumberInputError}</div>
                            ) : null}
                        </div>
                    ) : null}
                    {collectAddress ? (
                        <div
                            className={style(classes.formField, {})}
                        >
                            <DoppeActionWidgetTextBox
                                type={TextBoxTypes.Text}
                                name={'address'}
                                hasError={addressInputError !== undefined}
                                placeholder={doppeViewerLocales['contactFormAddressFieldPlaceholder']}
                                shouldValidateValue={false}
                                value={formController.getValue('address')}
                                onTextChange={value => {
                                    formController.setValue('address', value);
                                }}
                            />
                            {addressInputError !== undefined ? (
                                <div className={style(classes.inputErrorMessage, {})}>{addressInputError}</div>
                            ) : null}
                        </div>
                    ) : null}
                    {collectZip ? (
                        <div
                            className={style(classes.formField, {})}
                        >
                            <DoppeActionWidgetTextBox
                                type={TextBoxTypes.Text}
                                name={'zip'}
                                hasError={addressInputError !== undefined}
                                placeholder={doppeViewerLocales['contactFormZipCodeFieldPlaceholder']}
                                shouldValidateValue={false}
                                value={formController.getValue('zip')}
                                onTextChange={value => {
                                    formController.setValue('zip', value);
                                }}
                            />
                            {addressInputError !== undefined ? (
                                <div className={style(classes.inputErrorMessage, {})}>{zipInputError}</div>
                            ) : null}
                        </div>
                    ) : null}
                    {collectMessage ? (
                        <div
                            className={style(classes.formField, {})}
                        >
                            <DoppeActionWidgetTextArea
                                hasError={messageError !== undefined}
                                placeholder={doppeViewerLocales['contactFormMessageFieldPlaceholder']}
                                value={formController.getValue('message')}
                                onTextChange={value => {
                                    formController.setValue('message', value);
                                }}
                                minLines={4}
                                maxLines={8}
                            />
                            {messageError !== undefined ? (
                                <div className={style(classes.inputErrorMessage, {})}>{messageError}</div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
                <div>
                    <div
                        className={style(classes.consentBox, {hasError: formController.formFieldsErrors.hasConsent !== undefined}, FlexLayout.row.alignToTop)}>
                        {evaluateFunction(() => {
                            return showConsentCheckbox ?
                                (
                                    <Checkbox
                                        className={style(classes.consentCheckbox)}
                                        checked={formController.getValue('hasConsent')}
                                        onCheckedChanged={value => {
                                            formController.setValue('hasConsent', value);
                                        }}
                                        inputId={checkboxId}
                                    >
                                        {(renderProps) => {
                                            return (
                                                <div
                                                    className={style(classes.consentCheckboxView, {}, FlexLayout.centerContent)}
                                                >
                                                    {renderProps.checked ? (
                                                        <Icon
                                                            icon={IconChecked}
                                                        />
                                                    ) : null}
                                                </div>
                                            )
                                        }}
                                    </Checkbox>
                                ) :
                                null
                        })}
                        <label className={style(classes.consentText, {})}
                               htmlFor={checkboxId}>{consentText}</label>
                    </div>
                    {
                        formController.formFieldsErrors.hasConsent !== undefined ?
                            <div
                                className={style(classes.consentErrorMessageContainer, {})}
                            >
                                <div
                                    className={style(classes.inputErrorMessage, {})}
                                >
                                    {doppeViewerLocales['youMustAgree']}
                                </div>
                            </div>
                            :
                            null
                    }
                </div>
            </DoppeActionTypeWidgetContentLayoutNewWrapper>
        </DoppeActionTypeWidgetLayout>
    );
});